import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentCompany, setCurrentCompanyState] = useState(
    JSON.parse(localStorage.getItem('currentCompany') || 'null')
  );

  // Create axios instance with company header
  const getAuthHeaders = () => {
    const headers = { Authorization: `Bearer ${token}` };
    if (currentCompany?.id) {
      headers['X-Company-ID'] = currentCompany.id;
    }
    return headers;
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          if (currentCompany?.id) {
            headers['X-Company-ID'] = currentCompany.id;
          }
          const response = await axios.get(`${API}/auth/me`, { headers });
          setUser(response.data);
          
          // Set default company if not set
          if (!currentCompany && response.data.companies?.length > 0) {
            const defaultCompany = response.data.current_company || response.data.companies[0];
            setCurrentCompanyState(defaultCompany);
            localStorage.setItem('currentCompany', JSON.stringify(defaultCompany));
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentCompany');
          setToken(null);
          setUser(null);
          setCurrentCompanyState(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Set default company
    if (userData.current_company) {
      setCurrentCompanyState(userData.current_company);
      localStorage.setItem('currentCompany', JSON.stringify(userData.current_company));
    } else if (userData.companies?.length > 0) {
      const defaultCompany = { 
        id: userData.companies[0].id, 
        name: userData.companies[0].name,
        code: userData.companies[0].code
      };
      setCurrentCompanyState(defaultCompany);
      localStorage.setItem('currentCompany', JSON.stringify(defaultCompany));
    }
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentCompany');
    setToken(null);
    setUser(null);
    setCurrentCompanyState(null);
  };

  const switchCompany = async (company) => {
    setCurrentCompanyState(company);
    localStorage.setItem('currentCompany', JSON.stringify(company));
    
    // Refresh user data with new company context
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Company-ID': company.id
        }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    currentCompany,
    switchCompany,
    getAuthHeaders,
    isSuperAdmin: user?.is_super_admin || false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
